import { useEffect, useRef } from "react";
import { logger } from "@mwt/web-js-telemetry";

import { EventType, PageName } from "../types";

/**
 * This hook takes in two args namely `name` of the loading page and `isLoading`
 * which is a boolean flag for checking if the component has loaded.
 * By default the loading starts when the component starts rendering for the first time
 * and occurs only for once for component lifetime.
 * The page loading ends when the `isLoading` flag turns false and calculates the metric such
 * as duration, first-contentful-paint and other navigation information of the page using Performance
 * timing API and sends data to telemetry.
 *
 * @param {string} [name] Name of page for which the metrics is being logged.
 * @param {boolean} [isLoading] Flag for toggling isLoading of the pages.
 */
export const usePageLoadTimeTracker = (
  name: PageName,
  isLoading?: boolean
): void => {
  const componentFirstRender = useRef<boolean>(true);

  useEffect(() => {
    if (componentFirstRender.current && isLoading === false) {
      // nothing to test if performance isnt supported
      /* istanbul ignore else */
      if (window.performance) {
        const performance = window.performance;
        const performanceEntry = performance?.getEntriesByName(
          "first-contentful-paint"
        )?.[0];
        const fcp = performanceEntry?.startTime;
        const duration = performance.now();

        // Get Navigation Timing entry.
        const navigationEntry = performance?.getEntriesByType(
          "navigation"
        )?.[0] as unknown as PerformanceNavigationTiming;
        const contentLength = navigationEntry?.decodedBodySize;
        const hostname = window.location.hostname;
        const uri = window.location.pathname;
        const refUri = document.referrer;
        logger.logPageView(
          {
            name,
            uri,
            refUri,
            contentLength,
            hostname,
            duration,
            fcp,
          },
          // co-locate data with Virtual Visits. Logs all events to nonuievents.
          EventType.NonUiEvent
        );
      } else {
        logger.log("Performance timing isn't supported.");
      }
      componentFirstRender.current = false;
    }
  });
};
